import { FC, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Box,
    Stack,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Document, Page, Text, View, Image, Font } from '@react-pdf/renderer';
import { getPdfStylesForTemplate, PDF_HEADER_TITLE_ADDITION_COLOR } from './styles';
import { dualSharePdfPage1, isDualShareCompactLayout, isValueFocusCompactLayout, valueFocusPdfPage1 } from '../dualShareCompactLayout';
import { SectionHeaders, SECTION_HEADER_DEFAULTS } from '../PdfPreview';
import { normalizeMonthlyCommentary, splitMonthlyCommentaryLines, type ShareClassVisibilityMode } from '../mappers';
import {
    calcMonthlyCommentaryImageMaxHeight,
    calcMonthlyCommentaryImageWidth,
    calcPvmMonthlyCommentaryFontSizePdf,
} from '../monthlyCommentaryImage';
import {
    TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE,
    TEMPLATE_ID_DUAL_SHARE_CLASS,
    resolveDisclaimerText,
    resolveFussnoteText,
    resolveManImageUrl,
} from '../templateConstants';
import { pvmPdfPage1 } from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmCompactLayout';
import {
  isPvmGpsTemplate,
  resolvePvmGpsFondTitle,
  resolvePvmGpsHistoryLegendLabel,
} from '../../69d0c43f5a2b1c0d9e8f7a6b/templateConstants';
import { GpsPage2RightColumnPdf } from '../../69d0c43f5a2b1c0d9e8f7a6b/gpsPage2Pdf';
import { PvmPage1LeftColumnPdf } from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmPage1LeftColumn';
import { PvmPage2AwardImagesRowPdf, PvmPage2LeftPdf } from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmPage2Left';
import {
  PVM_GPS_CONTACT_LINES,
  PVM_GPS_LEFT_LOGO,
  PVM_GPS_RIGHT_LOGO,
  PVM_HEADER_LEFT_LOGO_WIDTH_PDF,
  PVM_HEADER_SIDE_COLUMN_WIDTH_PDF,
  PVM_HEADER_TOP_LOGO_WIDTH_PDF,
  resolvePvmGpsLeftLogoUrl,
} from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmHeader';
import {
  PVM_DONUT_COLORS,
  PVM_FONT_FILES,
  PVM_HEADLINE_COLOR,
  PVM_HEADLINE_FONT,
  PVM_BODY_FONT,
  PVM_TEXT_COLOR,
  resolvePvmHeadlineColor,
} from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmBranding';
import { fetchImageForPdf } from '../s3PdfImage';
import { S3_PREFIX } from '../s3PdfImage';
import { resolveTitleAddition } from '../titleAddition';
import { resolvePieChartHeading, type PieChartHeadingKey } from '../pieChartHeadings';
import { formatFactsheetHeaderDate, getPreiseDisplayRows } from '../factsheetDates';
import { isDualSharePriceField, resolveDualSharePriceRaw } from '../preiseFields';
import { getStammdatenRowLabel } from '../stammdatenFieldLabels';
import { KONDITIONEN_TER_ROW_LABEL } from '../konditionenFieldLabels';
import { getPieChartDisplayOrder } from '../pieChartDisplay';
import { normalizeMultilineFieldValue, splitMultilineFieldLines, templateShowsAnlageberater } from '../anlageberater';

import moment from 'moment';
import { client } from '../../../../../../App';
import { GET_AUTOMATED_COLUMNS } from '../../../../../../queries/widgets';
import { normalizeAutomatedColumnsInput } from '../../../../../../utils/normalizeAutomatedColumnsInput';
import { API_SERVER } from '../../../../../../contants';
import http from '../../../../../../apis/http';
import { registerReactPdfNoHyphenation, germanHyphenationCallback } from '../../reactPdfHyphenation';
import { PdfPreviewWithTools } from '../../../PdfPreviewWithTools';
import {
  buildFactsheetDownloadFileName,
  resolveFactsheetDownloadAsOf,
} from '../../../../../../utils/documentFileName';

// Re-asserted on every FactsheetPDF render — SQUAD (and others) may override the global callback.
// Global: no hyphenation. Monatskommentar Text nodes opt into German TeX/Typst patterns.
registerReactPdfNoHyphenation();

let pvmPdfFontsRegistered = false;
if (!pvmPdfFontsRegistered) {
  try {
    Font.register({
      family: PVM_HEADLINE_FONT,
      fonts: [
        { src: PVM_FONT_FILES.playfairRegular, fontWeight: 400 },
        { src: PVM_FONT_FILES.playfairBold, fontWeight: 700 },
      ],
    });
    Font.register({
      family: PVM_BODY_FONT,
      fonts: [
        { src: PVM_FONT_FILES.openSansRegular, fontWeight: 400 },
        { src: PVM_FONT_FILES.openSansBold, fontWeight: 700 },
      ],
    });
    pvmPdfFontsRegistered = true;
  } catch {
    // ignore duplicate registration
  }
}

const dialogStyles = {
    dialog: {
        '& .MuiDialog-paper': {
            width: '90vw',
            height: '90vh',
            maxWidth: '1200px',
            maxHeight: '900px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    dialogTitle: {
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px',
        '& .MuiTypography-root': {
            fontSize: '18px',
            fontWeight: 600,
        },
    },
    dialogContent: {
        padding: '0 !important',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    pdfContainer: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: '#525659',
    },
};

interface PdfPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    data: any;
    template: any;
    title?: string;
    isin?: string;
    until?: string;
}

const commafy = (num: number): string => {
    const str = num.toString().split('.');
    if (str[0].length >= 5) {
        str[0] = str[0].replace(/(\d)(?=(\d{3})+$)/g, '$1.');
    }
    if (str[1] && str[1].length >= 5) {
        str[1] = str[1].replace(/(\d{3})/g, '$1 ');
    }
    return str.join('.');
};

// Default business-table icons (same order as old PDF viewer)
const DEFAULT_ESG_ICONS = [
    './public/icon_1.png', './public/icon_8.png', './public/icon_5.png',
    './public/icon_4.png', './public/icon_10.png', './public/icon_2.png',
    './public/icon_7.png', './public/icon_3.png', './public/icon_6.png',
    './public/icon_9.png',
];

/** Get ESG item image URL with default icon fallback */
const getEsgItemImageUrl = (item: any, index: number): string => {
    if (item.image) {
        return item.image.includes('./public') ? `${API_SERVER}/${item.image}` : item.image;
    }
    const defaultIcon = DEFAULT_ESG_ICONS[index];
    return defaultIcon ? `${API_SERVER}/${defaultIcon}` : '';
};

// Chart color palette matching the factsheet screenshot
const DONUT_COLORS = ['#003f7a', '#1a6cb4', '#4a9fd9', '#58585a', '#a7a8ab'];

/** Format number as German-style percentage (comma as decimal separator) */
const formatPercent = (value: number | string): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return String(value);
    return num.toFixed(2).replace('.', ',') + '%';
};

/** Get color for chart item, falling back to the provided palette or the default palette */
const getItemColor = (item: any, index: number, palette?: string[]): string => {
    const colors = palette && palette.length > 0 ? palette : DONUT_COLORS;
    return item.color || colors[index % colors.length];
};

function isFactsheetDisplayEmptyPdf(display: string): boolean {
    const t = display.replace(/\r?\n+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!t) return true;
    if (t === '-' || t === '–' || t === '—') return true;
    if (/^n\/a$/i.test(t)) return true;
    return false;
}

function isDualRowHiddenPdf(a: string, b: string): boolean {
    return isFactsheetDisplayEmptyPdf(a) && isFactsheetDisplayEmptyPdf(b);
}

// PDF Document Component matching backend structure
export const FactsheetPDF: FC<{
    data: any;
    logoSrc?: string;
    rightLogoSrc?: string;
    manImageSrc?: string;
    page2ImageSrc?: string;
    page2ImageSrc2?: string;
    page2ImageSrc3?: string;
    cellDataCache?: Map<string, any>;
    esgIconCache?: Map<string, string>;
}> = ({ data, logoSrc, rightLogoSrc, manImageSrc, page2ImageSrc, page2ImageSrc2, page2ImageSrc3, cellDataCache, esgIconCache }) => {
    // SQUAD (imported later in templates/index) overrides the global hyphenation callback —
    // restore no-hyphenation for SHV/PVM; Monatskommentar Text opts into German patterns.
    registerReactPdfNoHyphenation();

    // Get template from data or use default (include _id when parent merges document.template)
    const template = data?.template || {};
    const isPvmGps = isPvmGpsTemplate(template?._id);
    const pdfStyles = getPdfStylesForTemplate(template?._id);
    const valueFocusCompact = isValueFocusCompactLayout(template?._id);
    const page1Compact = isDualShareCompactLayout(template?._id);
    const page1PdfGaps = isPvmGps
        ? pvmPdfPage1
        : valueFocusCompact
          ? valueFocusPdfPage1
          : page1Compact
            ? dualSharePdfPage1
            : null;
    const chartBlockMt = page1PdfGaps?.chartBlockMarginTop ?? 10;
    const chartImageMt = page1PdfGaps?.chartImageMarginTop ?? 5;
    const chartPlaceholderMt = page1PdfGaps?.chartPlaceholderMarginTop ?? 12;
    const contentHeaderMb = page1PdfGaps?.contentHeaderMarginBottom ?? 2;
    const effectiveData = data || {};
    const templateColor = resolvePvmHeadlineColor(template?._id, template?.color);
    const colorSets = isPvmGps
        ? PVM_DONUT_COLORS
        : (template?.colorSets && template.colorSets.length > 0 ? template.colorSets : undefined);
    const templateFond = resolvePvmGpsFondTitle(template?._id, template?.fond);
    const publishedAt = effectiveData.published_at || new Date();
    const intro = effectiveData.intro || [];
    const investmentphilosophie = effectiveData.investmentphilosophie || '';
    const preise = effectiveData.preise || {};
    const konditionen = effectiveData.konditionen || {};
    const stammdaten = effectiveData.stammdaten || {};
    const shareClassColumns: Array<{
        label?: string;
        isin?: string;
        preise?: Record<string, unknown>;
        konditionen?: Record<string, unknown>;
        stammdaten?: Record<string, unknown>;
    }> = Array.isArray(effectiveData.shareClassColumns) ? effectiveData.shareClassColumns : [];
    const dualShareClassPdf =
        template?._id === TEMPLATE_ID_DUAL_SHARE_CLASS && shareClassColumns.length >= 2;
    const dualVisibilityModeRaw =
        (effectiveData?.shareClassVisibilityMode as ShareClassVisibilityMode | undefined) ||
        ((effectiveData?.shareClassVisibility || {}) as Partial<
            Record<'preise' | 'konditionen' | 'stammdaten', ShareClassVisibilityMode>
        >)?.preise ||
        'both';
    const dualVisibilityMode: ShareClassVisibilityMode =
        dualVisibilityModeRaw === 'column0' || dualVisibilityModeRaw === 'column1'
            ? dualVisibilityModeRaw
            : 'both';
    const getVisibleColumnIndices = (): number[] => {
        if (dualVisibilityMode === 'column0') return [0];
        if (dualVisibilityMode === 'column1') return [1];
        return [0, 1];
    };
    const wertentwicklung_nach_perioden = effectiveData.wertentwicklung_nach_perioden;
    const risikoindikatoren = effectiveData.risikoindikatoren || null;
    const strategie = effectiveData.strategie || '';
    const chancen = effectiveData.chancen || '';
    const risiken = effectiveData.risiken || '';
    const ethik = effectiveData.ethik || '';
    const monthlyCommentaryNorm = normalizeMonthlyCommentary(effectiveData.monthlyCommentary, template?._id);
    const manImageWidthPdf = calcMonthlyCommentaryImageWidth(
        monthlyCommentaryNorm.left || String(intro?.[0] ?? ''),
        monthlyCommentaryNorm.right || String(intro?.[1] ?? ''),
    );
    const manImageMaxHeightPdf = calcMonthlyCommentaryImageMaxHeight(
        monthlyCommentaryNorm.left || String(intro?.[0] ?? ''),
        monthlyCommentaryNorm.right || String(intro?.[1] ?? ''),
    );
    const pvmCommentaryFontSize = isPvmGps
        ? calcPvmMonthlyCommentaryFontSizePdf(
              monthlyCommentaryNorm.left || String(intro?.[0] ?? ''),
              monthlyCommentaryNorm.right || String(intro?.[1] ?? ''),
          )
        : undefined;
    const manImageUrl = resolveManImageUrl(
        template?._id,
        effectiveData.manImageUrl,
        template?.manImageUrl,
    );
    const manImageIsPlaceholder =
        !manImageUrl ||
        manImageUrl === '/images/intro.png' ||
        manImageUrl.endsWith('/images/intro.png');
    // PVM: only show when a real custom image is set (no default intro.png).
    const manImageForPdf = isPvmGps
        ? manImageIsPlaceholder
            ? undefined
            : manImageSrc || manImageUrl
        : manImageSrc || manImageUrl;
    const businessTable = Array.isArray(effectiveData.business_table) ? effectiveData.business_table : [];
    const gesamtportfolio = effectiveData.gesamtportfolio || [];
    const branchen = effectiveData.branchen || [];
    const regionen = effectiveData.regionen || [];
    const marktkapitalisierung = effectiveData.marktkapitalisierung || [];
    const pie_addition = effectiveData.pie_addition || null;
    const title_addition = resolveTitleAddition(
        template?._id,
        effectiveData.title_addition,
        shareClassColumns,
    );
    const chartImageDataUrl = effectiveData.chartImageDataUrl;
    const historyLegendLabel = isPvmGps
        ? resolvePvmGpsHistoryLegendLabel(
              effectiveData.historyLegendLabel || effectiveData.wertentwicklung_seit_auflage_label,
          )
        : effectiveData.historyLegendLabel ||
          effectiveData.wertentwicklung_seit_auflage_label ||
          '';
    const historyTableLabels = effectiveData.wertentwicklung_seit_auflage_labels || [];
    const fafsHistoryTableLabel: any = page1Compact
        ? {
              label: effectiveData.historyLegendLabel || historyTableLabels?.[0]?.label || '',
              subLabel: effectiveData.historyLegendSubLabel || historyTableLabels?.[0]?.subLabel || '',
          }
        : null;
    const historyLegendColor = template?.color || '#003f7a';
    const chart1ImageDataUrl = effectiveData.chart1ImageDataUrl; // bar chart (wertentwicklung_nach_jahren)
    // const chart2ImageDataUrl = effectiveData.chart2ImageDataUrl; // horizontal-bar (gesamtportfolio)
    const headings = effectiveData.headings || {};
    const pieTitles = effectiveData.pie_titles;
    const pieHeading = (key: PieChartHeadingKey) =>
        resolvePieChartHeading(template?._id, key, { headings, pieTitles });
    const omitEthikKontroverse =
        template?._id === TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE || isPvmGps;
    const letzteAusschuettungLabel = 'Letzte Ausschüttung:';
    const sectionHeaders: SectionHeaders = {
        ...SECTION_HEADER_DEFAULTS,
        ...(effectiveData.sectionHeaders || {}),
    };

    const getDynamicSignature = (config: any): string => {
        try {
            return JSON.stringify(config?.fields || []);
        } catch {
            return '';
        }
    };

    // Helper to get automated column data – prefix distinguishes table (perf vs risk)
    const getAutomatedColumnValue = (cell: any, colIndex: number, rowIndex: number, prefix = ''): string => {
        let display = '';
        if (cell?.isDynamic && cell?.fields && cellDataCache) {
            const sig = getDynamicSignature(cell);
            const cellKey = prefix ? `${prefix}-${rowIndex}-${colIndex}-${sig}` : `${rowIndex}-${colIndex}-${sig}`;
            const fetchedValue = cellDataCache.get(cellKey);
            if (fetchedValue !== undefined && fetchedValue !== null) {
                display = String(fetchedValue);
            }
        }
        if (!display) {
            display = typeof cell === 'string' ? cell : (cell?.text || '');
        }
        if (prefix === 'perf' && colIndex > 0 && display && !display.trim().endsWith('%')) {
            display = `${display}%`;
        }
        return display;
    };

    // Render header component matching web preview structure
    const renderHeader = (isSecondPage = false) => (
        <View style={[pdfStyles.header, { marginTop: isSecondPage ? 80 : 0 }]}>
            {isPvmGps ? (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <Text style={[pdfStyles.headerTopText, { flex: 1, marginRight: 8 }]}>
                        Werbematerial, kein investmentrechtliches Pflichtdokument
                    </Text>
                    <Image
                        src={rightLogoSrc || PVM_GPS_RIGHT_LOGO}
                        style={{
                            width: PVM_HEADER_TOP_LOGO_WIDTH_PDF,
                            objectFit: 'contain',
                            objectPosition: 'right',
                        }}
                    />
                </View>
            ) : (
            <Text style={pdfStyles.headerTopText}>
                Werbematerial, kein investmentrechtliches Pflichtdokument
            </Text>
            )}

            <View style={pdfStyles.headerSplitter} />

            <View style={pdfStyles.headerContainer}>
                {isPvmGps ? (
                    <>
                        <View
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                flex: 1,
                                marginRight: 12,
                            }}
                        >
                            <Image
                                src={logoSrc || PVM_GPS_LEFT_LOGO}
                                style={{
                                    width: PVM_HEADER_LEFT_LOGO_WIDTH_PDF,
                                    marginRight: 12,
                                    objectFit: 'contain',
                                }}
                            />
                            <View style={{ flex: 1, justifyContent: 'center' }}>
                                <Text wrap={false} style={pdfStyles.headerTitleRow}>
                                    <Text style={pdfStyles.headerTitleText}>Facts & Figures</Text>
                                    <Text style={pdfStyles.headerDateText}>
                                        {' '}| {formatFactsheetHeaderDate(template?._id, publishedAt, shareClassColumns, preise, effectiveData)}
                                    </Text>
                                </Text>
                                <Text
                                    wrap={false}
                                    style={[pdfStyles.headerMainTitle, { color: templateColor, textAlign: 'left' }]}
                                >
                                    {templateFond}
                                    {title_addition && (
                                        <Text
                                            style={[
                                                pdfStyles.headerTitleAddition,
                                                { color: PDF_HEADER_TITLE_ADDITION_COLOR },
                                            ]}
                                        >
                                            {' '}{title_addition}
                                        </Text>
                                    )}
                                </Text>
                            </View>
                        </View>
                        <View
                            style={{
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                width: PVM_HEADER_SIDE_COLUMN_WIDTH_PDF,
                            }}
                        >
                            <View style={[pdfStyles.headerContactSection, { marginTop: 0 }]}>
                                {PVM_GPS_CONTACT_LINES.map((line, i) => (
                                    <Text
                                        key={i}
                                        style={[
                                            pdfStyles.headerContactText,
                                            ...(line.bold ? [pdfStyles.headerContactBold] : []),
                                            { textAlign: 'right' },
                                        ]}
                                    >
                                        {line.text}
                                    </Text>
                                ))}
                            </View>
                        </View>
                    </>
                ) : (
                    <>
                <View style={pdfStyles.headerLeftSection}>
                    <View style={pdfStyles.headerLogoSection}>
                        <View style={[pdfStyles.headerLogoRect1, { backgroundColor: templateColor }]} />
                        <View style={[pdfStyles.headerLogoRect2, { backgroundColor: templateColor }]}>
                            {logoSrc && (
                                <Image
                                    src={logoSrc}
                                    style={pdfStyles.headerLogoImage}
                                />
                            )}
                        </View>
                    </View>
                    <View style={pdfStyles.headerTitleSection}>
                        <Text wrap={false} style={pdfStyles.headerTitleRow}>
                            <Text style={pdfStyles.headerTitleText}>Facts & Figures</Text>
                            <Text style={pdfStyles.headerDateText}>
                                {' '}| {formatFactsheetHeaderDate(template?._id, publishedAt, shareClassColumns, preise, effectiveData)}
                            </Text>
                        </Text>
                        <Text style={[pdfStyles.headerMainTitle, { color: templateColor }]}>
                            {templateFond}
                            {title_addition && (
                                        <Text
                                            style={[
                                                pdfStyles.headerTitleAddition,
                                                { color: PDF_HEADER_TITLE_ADDITION_COLOR },
                                            ]}
                                        >
                                            {' '}| {title_addition}
                                        </Text>
                            )}
                        </Text>
                    </View>
                </View>
                <View style={pdfStyles.headerRightSection}>
                    <View style={pdfStyles.headerContactSection}>
                        <Text style={[pdfStyles.headerContactText, pdfStyles.headerContactBold]}>
                            Shareholder Value
                        </Text>
                        <Text style={[pdfStyles.headerContactText, pdfStyles.headerContactBold]}>
                            Management AG
                        </Text>
                        <Text style={pdfStyles.headerContactText}>Neue Mainzer Straße 1</Text>
                        <Text style={pdfStyles.headerContactText}>D - 60311 Frankfurt am Main</Text>
                        <Text style={pdfStyles.headerContactText}>Tel. +49 (0)69 66 98 30 0</Text>
                        <Text style={pdfStyles.headerContactText}>info@shareholdervalue.de</Text>
                        <Text style={[pdfStyles.headerContactText, pdfStyles.headerContactBold]}>
                            www.shareholdervalue.de
                        </Text>
                    </View>
                    <Image
                        src="/images/header-2.png"
                        style={pdfStyles.headerLogoRight}
                    />
                </View>
                    </>
                )}
            </View>

            <View style={pdfStyles.headerSplitter} />
        </View>
    );

    // Render key-value pair (omit row when value is empty or placeholder like "-")
    const renderKeyValue = (
        key: string,
        value: any,
        margin: number[] = [0, 0, 0, 0],
        options?: { preserveLineBreaks?: boolean },
    ) => {
        if (value === undefined || value === null) return null;
        const values = typeof value === 'string' && value.includes('###') ? value.split('###') : [value];
        if (!Array.isArray(values)) return null;
        const normalizeDisplay = (v: unknown) => {
            if (options?.preserveLineBreaks) {
                return normalizeMultilineFieldValue(v).trim();
            }
            return String(v).replace(/\r?\n+/g, ' ').replace(/\s+/g, ' ').trim();
        };
        const parts = values.map(normalizeDisplay);
        if (parts.every((p) => isFactsheetDisplayEmptyPdf(p))) return null;
        return (
            <View style={[pdfStyles.keyValueRow, { marginTop: margin[0], marginBottom: margin[2] }]}>
                <Text style={pdfStyles.keyValueKey}>{key}</Text>
                {parts.map((display, index) =>
                    options?.preserveLineBreaks ? (
                        <View key={index} style={index > 0 ? { marginLeft: 3 } : undefined}>
                            {renderMultilinePdfValue(display, pdfStyles.keyValueValue)}
                        </View>
                    ) : (
                        <Text
                            key={index}
                            style={[
                                pdfStyles.keyValueValue,
                                ...(index > 0 ? [{ marginLeft: 3 }] : []),
                            ]}
                        >
                            {display}
                    </Text>
                    ),
                )}
            </View>
        );
    };

    const resolveKvDynamicValue = (configOrValue: any, kvKey: string, cacheSuffix = ''): any => {
        if (configOrValue?.isDynamic && cellDataCache) {
            const key = `${kvKey}-${getDynamicSignature(configOrValue)}${cacheSuffix ? `-${cacheSuffix}` : ''}`;
            const fetched = cellDataCache.get(key);
            if (fetched !== undefined && fetched !== null) return fetched;
            return configOrValue?.fields?.[0]?.text || '';
        }
        return configOrValue;
    };

    /** PVM left column: materialize dynamic NAV / Fondsvermögen from cellDataCache */
    const pvmLeftColumnPdf = (() => {
        if (!isPvmGps) return effectiveData;
        const preiseSrc = { ...(effectiveData.preise || {}) };
        const fondsvolumen = resolveKvDynamicValue(preiseSrc.fondsvolumen, 'kv-preise-fondsvolumen');
        const ruecknahme = resolveKvDynamicValue(preiseSrc.ruecknahmepreis, 'kv-preise-ruecknahmepreis');
        const mepreis = resolveKvDynamicValue(preiseSrc.mepreis, 'kv-preise-mepreis');
        if (fondsvolumen && (preiseSrc.fondsvolumen as any)?.isDynamic) {
            preiseSrc.fondsvolumen = fondsvolumen;
        }
        if (ruecknahme && String(ruecknahme).trim()) {
            preiseSrc.ruecknahmepreis = ruecknahme;
        } else if (mepreis && String(mepreis).trim()) {
            preiseSrc.ruecknahmepreis = mepreis;
        }
        return { ...effectiveData, preise: preiseSrc };
    })();

    const formatMepreisPdf = (raw: any) => {
        if (typeof raw === 'string') return raw;
        if (raw === undefined || raw === null || raw === '') return '';
        return `${String(raw).replace(/\./g, ',')} EUR`;
    };

    const formatFondsvolumenPdf = (raw: any) => {
        if (typeof raw === 'string') {
            return /^[0-9]+$/.test(raw)
                ? `${commafy(Math.floor(+raw))} EUR`
                : raw;
        }
        return `${commafy(Math.floor(Number(raw) || 0))} EUR`;
    };

    const formatPreiseCellPdf = (key: string, raw: unknown): string => {
        if (raw === undefined || raw === null) return '';
        if (key === 'mepreis' || isDualSharePriceField(key)) return formatMepreisPdf(raw);
        if (key === 'fondsvolumen') return formatFondsvolumenPdf(raw);
        return String(raw);
    };

    const buildPreiseDisplayPdf = (preiseCol: any, colSuff: string) => {
        const dualSharePdf = template?._id === TEMPLATE_ID_DUAL_SHARE_CLASS;
        const combinedMepreis = resolveKvDynamicValue(preiseCol?.mepreis, 'kv-preise-mepreis', colSuff);
        const base = {
            ...preiseCol,
            ...(dualSharePdf
                ? {
                      ausgabepreis: resolveDualSharePriceRaw(
                          'ausgabepreis',
                          resolveKvDynamicValue(preiseCol?.ausgabepreis, 'kv-preise-ausgabepreis', colSuff),
                          combinedMepreis,
                      ),
                      ruecknahmepreis: resolveDualSharePriceRaw(
                          'ruecknahmepreis',
                          resolveKvDynamicValue(preiseCol?.ruecknahmepreis, 'kv-preise-ruecknahmepreis', colSuff),
                          combinedMepreis,
                      ),
                  }
                : {
                      mepreis: combinedMepreis,
                  }),
            fondsvolumen: resolveKvDynamicValue(preiseCol?.fondsvolumen, 'kv-preise-fondsvolumen', colSuff),
        };
        const mapped = Object.entries(base).map(([k, v]) => {
            if (k === 'letzte') return ['Letzte Ausschüttung', v];
            return [k, v];
        });
        const filtered = mapped.filter(([k, v]) => {
            if (k !== 'Letzte Ausschüttung') return true;
            return v !== undefined && v !== null && String(v).trim() !== '';
        });
        return Object.fromEntries(filtered);
    };

    const fmtStammdatenPdf = (k: string, v: unknown) => {
        if (k === 'auflagedatum' && v) {
            const m = moment(v as string);
            return m.isValid() ? m.format('DD.MM.YYYY') : String(v);
        }
        if (v === undefined || v === null) return '';
        if (k === 'anlageberater') return normalizeMultilineFieldValue(v).trim();
        return String(v);
    };

    const stammdatenMultilineStyle = { lineHeight: 1.12, marginBottom: 0.5 };

    const renderMultilinePdfValue = (value: unknown, baseStyle: any | any[]) => {
        const lines = splitMultilineFieldLines(value);
        if (lines.length === 0) return null;
        const styles = Array.isArray(baseStyle) ? baseStyle : [baseStyle];
        // Column layout on wrapper only — do not also pad each Text line (that double-indents).
        const wrapperStyle = styles.map((s) => ({
            width: s?.width,
            flex: s?.flex,
            paddingLeft: s?.paddingLeft,
            paddingRight: s?.paddingRight,
            marginLeft: s?.marginLeft,
        }));
        const textStyle = styles.map((s) => ({
            fontSize: s?.fontSize,
            color: s?.color,
            fontWeight: s?.fontWeight,
            textAlign: s?.textAlign,
        }));
        return (
            <View style={[{ flexDirection: 'column', flexShrink: 1 }, ...wrapperStyle]}>
                {lines.map((line, index) => (
                    <Text
                        key={`${line}-${index}`}
                        style={[
                            ...textStyle,
                            stammdatenMultilineStyle,
                            ...(index === lines.length - 1 ? [{ marginBottom: 0 }] : []),
                        ]}
                    >
                        {line}
                    </Text>
                ))}
            </View>
        );
    };

    const renderContentHeader = (title: string, color?: string) => (
        <View style={{ marginBottom: contentHeaderMb }}>
            <Text style={[pdfStyles.sectionTitle, { color: color || (isPvmGps ? PVM_HEADLINE_COLOR : '#6D6E70') }]}>{title}</Text>
        </View>
    );

    return (
        <Document>
            {/* First Page */}
            <Page size="A4" style={pdfStyles.page}>
                {renderHeader(false)}

                {/* Page 1 hero: monthly left/right when set (same as page 2 body); else legacy intro */}
                {(monthlyCommentaryNorm.left || monthlyCommentaryNorm.right) ? (
                    <View style={pdfStyles.introSection}>
                        {manImageForPdf ? (
                            <View style={pdfStyles.introImageWrap}>
                                <Image
                                    src={manImageForPdf}
                                    style={{
                                        width: manImageWidthPdf,
                                        maxHeight: manImageMaxHeightPdf,
                                        objectFit: 'contain',
                                        marginRight: 10,
                                    }}
                                />
                        </View>
                        ) : null}
                        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'flex-start' }}>
                            <View style={[pdfStyles.introTextColumn, { paddingRight: 6 }]}>
                                {splitMonthlyCommentaryLines(monthlyCommentaryNorm.left).map((line, i, arr) => (
                                    <View
                                        key={`mc-l-${i}`}
                                        style={[
                                            pdfStyles.introTextLineWrap,
                                            {
                                                // Enter gap ≈ soft-wrap (lineHeight); blank Enter keeps a small paragraph gap
                                                marginBottom:
                                                    i < arr.length - 1 ? (line === '' ? 4 : 0) : 0,
                                            },
                                        ]}
                                    >
                                        <Text
                                            hyphenationCallback={germanHyphenationCallback}
                                            style={[
                                                pdfStyles.introTextLine,
                                                ...(pvmCommentaryFontSize != null
                                                    ? [{ fontSize: pvmCommentaryFontSize }]
                                                    : []),
                                            ]}
                                        >
                                            {line === '' ? ' ' : line}
                                        </Text>
                                    </View>
                                ))}
                            </View>
                            <View style={[pdfStyles.introTextColumn, { paddingLeft: 6 }]}>
                                {splitMonthlyCommentaryLines(monthlyCommentaryNorm.right).map((line, i, arr) => (
                                    <View
                                        key={`mc-r-${i}`}
                                        style={[
                                            pdfStyles.introTextLineWrap,
                                            {
                                                marginBottom:
                                                    i < arr.length - 1 ? (line === '' ? 4 : 0) : 0,
                                            },
                                        ]}
                                    >
                                        <Text
                                            hyphenationCallback={germanHyphenationCallback}
                                            style={[
                                                pdfStyles.introTextLine,
                                                ...(pvmCommentaryFontSize != null
                                                    ? [{ fontSize: pvmCommentaryFontSize }]
                                                    : []),
                                            ]}
                                        >
                                            {line === '' ? ' ' : line}
                                        </Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                ) : (
                    intro.length >= 2 && (
                        <View style={pdfStyles.introSection}>
                            {manImageForPdf ? (
                                <View style={pdfStyles.introImageWrap}>
                                    <Image
                                        src={manImageForPdf}
                                        style={{
                                            width: manImageWidthPdf,
                                            maxHeight: manImageMaxHeightPdf,
                                            objectFit: 'contain',
                                            marginRight: 10,
                                        }}
                                    />
                            </View>
                            ) : null}
                            <Text
                                hyphenationCallback={germanHyphenationCallback}
                                style={[pdfStyles.introText, { flex: 1 }]}
                            >
                                {intro[0]}
                            </Text>
                            <Text
                                hyphenationCallback={germanHyphenationCallback}
                                style={[pdfStyles.introText, { flex: 1, marginLeft: 10 }]}
                            >
                                {intro[1]}
                            </Text>
                        </View>
                    )
                )}

                <View style={pdfStyles.page1VerticalFill}>
                        {/* Two columns fill remaining page height; footnotes sit at bottom of right column */}
                        <View style={[pdfStyles.twoColumnLayout, { flex: 1, alignItems: 'stretch', minHeight: 0 }]}>
                    {/* Left Column */}
                    <View style={[pdfStyles.leftColumn, { alignSelf: 'stretch' }]}>
                        {isPvmGps ? (
                            <PvmPage1LeftColumnPdf
                                pdf={pvmLeftColumnPdf}
                                pdfStyles={pdfStyles}
                                templateColor={templateColor}
                                renderContentHeader={renderContentHeader}
                                investmentphilosophieTitle={sectionHeaders.investmentphilosophie}
                                strategieTitle={sectionHeaders.strategie}
                            />
                        ) : (
                        <>
                        {investmentphilosophie.trim() ? (
                            <View style={pdfStyles.leftColumnSection}>
                        {renderContentHeader(sectionHeaders.investmentphilosophie)}
                                <Text style={[pdfStyles.sectionText, { marginBottom: 0 }]}>{investmentphilosophie}</Text>
                            </View>
                        ) : null}

                        {/* Strategie */}
                        <View style={pdfStyles.leftColumnSection}>
                        {renderContentHeader(sectionHeaders.strategie, templateColor)}
                            <Text style={[pdfStyles.sectionText, { marginBottom: 0 }]}>{strategie}</Text>
                        </View>

                        {dualShareClassPdf && shareClassColumns[0] && shareClassColumns[1] ? (
                            <>
                                {/* Prices — dual share classes */}
                                <View style={pdfStyles.leftColumnSection}>
                        {renderContentHeader(sectionHeaders.preise, templateColor)}
                                    {(() => {
                                        const visible = getVisibleColumnIndices();
                                        if (visible.length === 2) {
                                            return (
                                                <View style={pdfStyles.keyValueTripleRow}>
                                                    <Text style={pdfStyles.keyValueTripleLabel} />
                                                    <Text style={pdfStyles.shareClassHeader}>
                                                        {shareClassColumns[visible[0]].label || '—'}
                                                    </Text>
                                                    <Text style={pdfStyles.shareClassHeader}>
                                                        {shareClassColumns[visible[1]].label || '—'}
                                                    </Text>
                                                </View>
                                            );
                                        }
                                        return (
                                            <View style={pdfStyles.keyValueRow}>
                                                <Text style={pdfStyles.keyValueKey} />
                                                <Text style={pdfStyles.keyValueValue}>
                                                    {shareClassColumns[visible[0]].label || '—'}
                                                </Text>
                                            </View>
                                        );
                                    })()}
                                    {(() => {
                                        const visible = getVisibleColumnIndices();
                                        const p0 = buildPreiseDisplayPdf(
                                            shareClassColumns[visible[0]].preise || {},
                                            `sc${visible[0]}`,
                                        );
                                        const p1 =
                                            visible.length === 2
                                                ? buildPreiseDisplayPdf(
                                                      shareClassColumns[visible[1]].preise || {},
                                                      `sc${visible[1]}`,
                                                  )
                                                : null;
                                        const out: ReactNode[] = [];
                                        getPreiseDisplayRows(template?._id).forEach(([key, label]) => {
                                            const raw0 =
                                                key === 'Letzte Ausschüttung'
                                                    ? p0['Letzte Ausschüttung']
                                                    : (p0 as Record<string, unknown>)[key];
                                            const raw1 =
                                                p1
                                                    ? key === 'Letzte Ausschüttung'
                                                        ? p1['Letzte Ausschüttung']
                                                        : (p1 as Record<string, unknown>)[key]
                                                    : '';
                                            const fk = key === 'Letzte Ausschüttung' ? 'letzte' : key;
                                            const d0 = formatPreiseCellPdf(fk, raw0);
                                            const d1 = formatPreiseCellPdf(fk, raw1);
                                            if (visible.length === 2) {
                                                if (isDualRowHiddenPdf(d0, d1)) return;
                                                out.push(
                                                    <View key={key} style={pdfStyles.keyValueTripleRow} wrap={false}>
                                                        <Text style={pdfStyles.keyValueTripleLabel}>{label}</Text>
                                                        <Text style={pdfStyles.keyValueTripleCell}>{d0}</Text>
                                                        <Text style={pdfStyles.keyValueTripleCell}>{d1}</Text>
                                                    </View>,
                                                );
                                                return;
                                            }
                                            if (isFactsheetDisplayEmptyPdf(d0)) return;
                                            out.push(
                                                <View key={key} style={pdfStyles.keyValueRow} wrap={false}>
                                                    <Text style={pdfStyles.keyValueKey}>{label}</Text>
                                                    <Text style={pdfStyles.keyValueValue}>{d0}</Text>
                                                </View>,
                                            );
                                        });
                                        return out;
                                    })()}
                                </View>

                                {/* Conditions — dual */}
                                <View style={pdfStyles.leftColumnSection}>
                                    {renderContentHeader(sectionHeaders.konditionen, templateColor)}
                                    {(() => {
                                        const visible = getVisibleColumnIndices();
                                        if (visible.length === 2) {
                                            return (
                                                <View style={pdfStyles.keyValueTripleRow}>
                                                    <Text style={pdfStyles.keyValueTripleLabel} />
                                                    <Text style={pdfStyles.shareClassHeader}>
                                                        {shareClassColumns[visible[0]].label || '—'}
                                                    </Text>
                                                    <Text style={pdfStyles.shareClassHeader}>
                                                        {shareClassColumns[visible[1]].label || '—'}
                                                    </Text>
                                                </View>
                                            );
                                        }
                                        return (
                                            <View style={pdfStyles.keyValueRow}>
                                                <Text style={pdfStyles.keyValueKey} />
                                                <Text style={pdfStyles.keyValueValue}>
                                                    {shareClassColumns[visible[0]].label || '—'}
                                                </Text>
                                            </View>
                                        );
                                    })()}
                                    {(
                                        [
                                            ['laufendeKosten', 'Laufende Kosten:'],
                                            ['verwaltungsgebühr', 'Verwaltungsgebühr:'],
                                            ['verwaltungsvergütung', 'Verwaltungsvergütung:'],
                                            ['performance_fee', 'Performance Fee:'],
                                            ['ter_2022', KONDITIONEN_TER_ROW_LABEL],
                                            ['ausgabeaufschlag', 'Ausgabeaufschlag:'],
                                        ] as const
                                    ).flatMap(([k, label]) => {
                                        const visible = getVisibleColumnIndices();
                                        const d0 = String(
                                            (shareClassColumns[visible[0]].konditionen as Record<string, unknown>)?.[k] ??
                                                '',
                                        );
                                        const d1 = String(
                                            (shareClassColumns[visible[1]]?.konditionen as Record<string, unknown>)?.[k] ??
                                                '',
                                        );
                                        if (visible.length === 2 && isDualRowHiddenPdf(d0, d1)) return [];
                                        if (visible.length === 1 && isFactsheetDisplayEmptyPdf(d0)) return [];
                                        if (visible.length === 1) {
                                            return [
                                                <View key={k} style={pdfStyles.keyValueRow} wrap={false}>
                                                    <Text style={pdfStyles.keyValueKey}>{label}</Text>
                                                    <Text style={pdfStyles.keyValueValue}>{d0.trim()}</Text>
                                                </View>,
                                            ];
                                        }
                                        return [
                                            <View key={k} style={pdfStyles.keyValueTripleRow} wrap={false}>
                                                <Text style={pdfStyles.keyValueTripleLabel}>{label}</Text>
                                                <Text style={pdfStyles.keyValueTripleCell}>{d0.trim()}</Text>
                                                <Text style={pdfStyles.keyValueTripleCell}>{d1.trim()}</Text>
                                            </View>,
                                        ];
                                    })}
                                </View>

                                {/* Master Data — dual */}
                                <View style={[pdfStyles.leftColumnSection, { marginBottom: 0 }]}>
                                    {renderContentHeader(sectionHeaders.stammdaten, templateColor)}
                                    {(() => {
                                        const visible = getVisibleColumnIndices();
                                        if (visible.length === 2) {
                                            return (
                                                <View style={pdfStyles.keyValueTripleRow}>
                                                    <Text style={pdfStyles.keyValueTripleLabel} />
                                                    <Text style={pdfStyles.shareClassHeader}>
                                                        {shareClassColumns[visible[0]].label || '—'}
                                                    </Text>
                                                    <Text style={pdfStyles.shareClassHeader}>
                                                        {shareClassColumns[visible[1]].label || '—'}
                                                    </Text>
                                                </View>
                                            );
                                        }
                                        return (
                                            <View style={pdfStyles.keyValueRow}>
                                                <Text style={pdfStyles.keyValueKey} />
                                                <Text style={pdfStyles.keyValueValue}>
                                                    {shareClassColumns[visible[0]].label || '—'}
                                                </Text>
                                            </View>
                                        );
                                    })()}
                                    {(
                                        [
                                            ['isin', 'ISIN:'],
                                            ['wkn', 'WKN:'],
                                            ['bloomberg', 'Bloomberg:'],
                                            ['reuters', 'Reuters:'],
                                            ['fondsart', 'Fondsart:'],
                                            ['nachhaltigkeit', 'Nachhaltigkeit:'],
                                            ['anlageschwerpunkt', 'Anlageschwerpunkt:'],
                                            ['srri', 'SRI:'],
                                            ['vergleichsindex', 'Vergleichsindex:'],
                                            ['auflagedatum', 'Auflagedatum:'],
                                            ['anlageberater', 'Anlageberater:'],
                                            [
                                                'verwaltungsgesellschaft',
                                                getStammdatenRowLabel(
                                                    template?._id,
                                                    'verwaltungsgesellschaft',
                                                    'Verwaltungsgesellschaft:',
                                                ),
                                            ],
                                            ['verwahrstelle', 'Verwahrstelle:'],
                                            ['geschäftsjahr', 'Geschäftsjahr:'],
                                            ['vertriebszulassung', 'Vertriebszulassung:'],
                                        ] as const
                                    ).flatMap(([k, label]) => {
                                        if (k === 'anlageberater' && !templateShowsAnlageberater(template?._id)) {
                                            return [];
                                        }
                                        const visible = getVisibleColumnIndices();
                                        const d0 = fmtStammdatenPdf(
                                            k,
                                            (shareClassColumns[visible[0]].stammdaten as Record<string, unknown>)?.[k],
                                        );
                                        const d1 = fmtStammdatenPdf(
                                            k,
                                            (shareClassColumns[visible[1]]?.stammdaten as Record<string, unknown>)?.[k],
                                        );
                                        if (visible.length === 2 && isDualRowHiddenPdf(d0, d1)) return [];
                                        if (visible.length === 1 && isFactsheetDisplayEmptyPdf(d0)) return [];
                                        if (visible.length === 1) {
                                            return [
                                                <View key={k} style={pdfStyles.keyValueRow} wrap={false}>
                                                    <Text style={pdfStyles.keyValueKey}>{label}</Text>
                                                    {k === 'anlageberater'
                                                        ? renderMultilinePdfValue(d0, pdfStyles.keyValueValue)
                                                        : (
                                                            <Text style={pdfStyles.keyValueValue}>
                                                                {d0}
                                                            </Text>
                                                        )}
                                                </View>,
                                            ];
                                        }
                                        return [
                                            <View key={k} style={pdfStyles.keyValueTripleRow} wrap={false}>
                                                <Text style={pdfStyles.keyValueTripleLabel}>{label}</Text>
                                                {k === 'anlageberater'
                                                    ? renderMultilinePdfValue(d0, pdfStyles.keyValueTripleCell)
                                                    : (
                                                        <Text style={pdfStyles.keyValueTripleCell}>
                                                            {d0}
                                                        </Text>
                                                    )}
                                                {k === 'anlageberater'
                                                    ? renderMultilinePdfValue(d1, pdfStyles.keyValueTripleCell)
                                                    : (
                                                        <Text style={pdfStyles.keyValueTripleCell}>
                                                            {d1}
                                                        </Text>
                                                    )}
                                            </View>,
                                        ];
                                    })}
                                </View>
                            </>
                        ) : (
                            <>
                                {/* Prices */}
                                <View style={[pdfStyles.leftColumnSection, { marginBottom: 0 }]}>
                                    {renderContentHeader(sectionHeaders.preise, templateColor)}
                                    {(() => {
                                        const p0 = buildPreiseDisplayPdf(preise, '');
                                        const out: ReactNode[] = [];
                                        getPreiseDisplayRows(template?._id).forEach(([key, label]) => {
                                            const raw0 =
                                                key === 'Letzte Ausschüttung'
                                                    ? p0['Letzte Ausschüttung']
                                                    : (p0 as Record<string, unknown>)[key];
                                            const fk = key === 'Letzte Ausschüttung' ? 'letzte' : key;
                                            const d0 = formatPreiseCellPdf(fk, raw0);
                                            if (isFactsheetDisplayEmptyPdf(d0)) return;
                                            const displayLabel =
                                                key === 'Letzte Ausschüttung' ? letzteAusschuettungLabel : label;
                                            out.push(
                                                <View key={key} style={pdfStyles.keyValueRow} wrap={false}>
                                                    <Text style={pdfStyles.keyValueKey}>{displayLabel}</Text>
                                                    <Text style={pdfStyles.keyValueValue}>{d0}</Text>
                                                </View>,
                                            );
                                        });
                                        return out;
                                    })()}
                                </View>

                        {/* Conditions */}
                                <View style={pdfStyles.leftColumnSection}>
                        {renderContentHeader(sectionHeaders.konditionen, templateColor)}
                        {konditionen.laufendeKosten &&
                                        renderKeyValue('Laufende Kosten:', konditionen.laufendeKosten)}
                        {konditionen.verwaltungsgebühr && (
                            <>
                                            {renderKeyValue('Verwaltungsgebühr:', konditionen.verwaltungsgebühr)}
                                {renderKeyValue('Verwaltungsvergütung:', konditionen.verwaltungsvergütung)}
                                {renderKeyValue('Performance Fee:', konditionen.performance_fee)}
                                            {renderKeyValue(KONDITIONEN_TER_ROW_LABEL, konditionen.ter_2022)}
                                {renderKeyValue('Ausgabeaufschlag:', konditionen.ausgabeaufschlag)}
                            </>
                        )}
                                </View>

                        {/* Master Data */}
                                <View style={[pdfStyles.leftColumnSection, { marginBottom: 0 }]}>
                        {renderContentHeader(sectionHeaders.stammdaten, templateColor)}
                                    {renderKeyValue('ISIN:', stammdaten.isin)}
                        {renderKeyValue('WKN:', stammdaten.wkn)}
                        {renderKeyValue('Bloomberg:', stammdaten.bloomberg)}
                        {stammdaten.reuters && renderKeyValue('Reuters:', stammdaten.reuters)}
                        {renderKeyValue('Fondsart:', stammdaten.fondsart)}
                        {renderKeyValue('Nachhaltigkeit:', stammdaten.nachhaltigkeit)}
                        {renderKeyValue('Anlageschwerpunkt:', stammdaten.anlageschwerpunkt)}
                        {stammdaten.srri && renderKeyValue('SRI:', stammdaten.srri)}
                                    {stammdaten.vergleichsindex &&
                                        renderKeyValue('Vergleichsindex:', stammdaten.vergleichsindex)}
                        {renderKeyValue(
                            'Auflagedatum:',
                            stammdaten.auflagedatum
                                ? moment(stammdaten.auflagedatum).format('DD.MM.YYYY')
                                            : stammdaten.auflagedatum,
                                    )}
                                    {templateShowsAnlageberater(template?._id) &&
                                        (stammdaten.anlageberater != null &&
                                        String(stammdaten.anlageberater).trim() !== '' ? (
                                            <View style={pdfStyles.keyValueRow} wrap={false}>
                                                <Text style={pdfStyles.keyValueKey}>Anlageberater:</Text>
                                                {renderMultilinePdfValue(stammdaten.anlageberater, pdfStyles.keyValueValue)}
                                            </View>
                                        ) : null)}
                                    {renderKeyValue(
                                        getStammdatenRowLabel(
                                            template?._id,
                                            'verwaltungsgesellschaft',
                                            'Verwaltungsgesellschaft:',
                                        ),
                                        stammdaten.verwaltungsgesellschaft,
                                    )}
                        {renderKeyValue(
                            'Verwahrstelle:',
                            stammdaten.verwahrstelle,
                                        [0, 0, 0, 0],
                        )}
                        {stammdaten.geschäftsjahr &&
                            renderKeyValue(
                                'Geschäftsjahr:',
                                stammdaten.geschäftsjahr,
                                            [0, 0, 0, 0],
                            )}
                        {stammdaten.vertriebszulassung &&
                                        renderKeyValue('Vertriebszulassung:', stammdaten.vertriebszulassung, [
                                            0, 0, 0, 0,
                                        ])}
                                </View>
                            </>
                        )}
                        </>
                        )}
                    </View>

                    {/* Right Column - Performance Charts */}
                    <View
                        style={[
                            pdfStyles.rightColumn,
                            {
                                alignSelf: 'stretch',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                minHeight: 0,
                            },
                        ]}
                    >
                        <View style={{ flexGrow: 1, flexShrink: 1 }}>
                        {/* Performance since launch */}
                        <View>
                            <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                                <View style={[pdfStyles.sectionTitle, { flexDirection: 'row', alignItems: 'flex-start' }]}>
                                    <Text>
                                        {sectionHeaders.wertentwicklungSeitAuflage}¹
                                    </Text>
                                </View>
                            </View>
                            {chartImageDataUrl ? (
                                <Image src={chartImageDataUrl} style={{ width: '100%', marginTop: chartImageMt }} />
                            ) : (
                                <View style={{ marginTop: chartPlaceholderMt, height: 100, backgroundColor: '#f0f0f0' }}>
                                    <Text style={{ fontSize: 8, textAlign: 'center', paddingTop: 40 }}>
                                        Performance Chart
                                    </Text>
                                </View>
                            )}
                            {fafsHistoryTableLabel?.label ? (
                                <View
                                    style={{
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        marginTop: 4,
                                        marginLeft: 12,
                                    }}
                                >
                                    <View
                                        style={{
                                            width: 16,
                                            height: 2.5,
                                            backgroundColor: historyLegendColor,
                                            marginRight: 6,
                                        }}
                                    />
                                    <View style={{ flexDirection: 'column' }}>
                                        <Text style={{ fontSize: 8, color: '#000000', lineHeight: 1.3 }}>
                                            {fafsHistoryTableLabel.label}
                                        </Text>
                                        {fafsHistoryTableLabel.subLabel ? (
                                            <Text style={{ fontSize: 8, color: '#000000', lineHeight: 1.3 }}>
                                                {fafsHistoryTableLabel.subLabel}
                                            </Text>
                                        ) : null}
                        </View>
                                </View>
                            ) : historyLegendLabel ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, marginLeft: 12 }}>
                                    <View
                                        style={{
                                            width: 16,
                                            height: 2.5,
                                            backgroundColor: historyLegendColor,
                                            marginRight: 6,
                                        }}
                                    />
                                    <Text style={{ fontSize: 8, color: '#000000' }}>
                                        {historyLegendLabel}
                            </Text>
                                </View>
                            ) : null}
                        </View>

                        {/* Performance by years */}
                        <View style={{ marginTop: chartBlockMt }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%' }}>
                                <View style={[pdfStyles.sectionTitle, { flexDirection: 'row', alignItems: 'flex-start', width: '100%' }]}>
                                    <Text>
                                        {sectionHeaders.wertentwicklungNachJahren}¹
                                    </Text>
                                </View>
                            </View>
                            {chart1ImageDataUrl ? (
                                <Image src={chart1ImageDataUrl} style={{ width: '100%', marginTop: chartImageMt }} />
                            ) : (
                                <View style={{ marginTop: chartPlaceholderMt, height: 80, backgroundColor: '#f0f0f0' }}>
                                    <Text style={{ fontSize: 8, textAlign: 'center', paddingTop: 30 }}>
                                        Years Chart (loading...)
                                    </Text>
                                </View>
                            )}
                        </View>

                        {/* <View style={{ marginTop: 10 }}>
                            <Text style={{ fontSize: 8, marginLeft: 15 }}>
                                Frankfurter UCITS-ETF - Modern Value
                            </Text>
                        </View> */}

                        {/* Performance by periods */}
                        {wertentwicklung_nach_perioden && (() => {
                            let labels: string[] = [];
                            let rawValues: any[][] = [];

                            if (wertentwicklung_nach_perioden.labels) {
                                labels = wertentwicklung_nach_perioden.labels;
                                rawValues = Array.isArray(wertentwicklung_nach_perioden.values) ? wertentwicklung_nach_perioden.values : [];
                            } else if (Array.isArray(wertentwicklung_nach_perioden)) {
                                labels = wertentwicklung_nach_perioden.map((item: any) => item.label);
                                rawValues = [wertentwicklung_nach_perioden.map((item: any) => String(item.value))];
                            }

                            if (labels.length === 0) return null;

                            return (
                                <View style={{ marginTop: chartBlockMt }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                        <Text style={pdfStyles.sectionTitle}>{sectionHeaders.wertentwicklungNachPerioden}¹
                                        </Text>
                                    </View>
                                    {/* Header row */}
                                    <View style={{ flexDirection: 'row', marginTop: chartBlockMt, paddingBottom: 0 }}>
                                        {labels.map((label: string, i: number) => (
                                            <View key={i} style={{ flex: 1 }}>
                                                <Text style={{
                                                    fontSize: 7,
                                                    fontWeight: 'bold',
                                                    color: '#000000',
                                                    textAlign: i === 0 ? 'left' : 'center',
                                                    lineHeight: 1,
                                                }}>
                                                    {label}
                                                </Text>
                                            </View>
                                        ))}
                                    </View>
                                    {/* Value rows */}
                                    {rawValues.map((row: any[], rowIdx: number) => (
                                        <View key={rowIdx} style={{ flexDirection: 'row', marginTop: 2, gap: 2 }}>
                                            {row.map((cell: any, i: number) => (
                                                <View key={i} style={{
                                                    flex: 1,
                                                    backgroundColor: '#D2D2D4',
                                                    paddingVertical: 1,
                                                    paddingHorizontal: 4,
                                                }}>
                                                    <Text style={{
                                                        fontSize: 7,
                                                        color: '#000000',
                                                        textAlign: i === 0 ? 'left' : 'center',
                                                        lineHeight: 1,
                                                    }}>
                                                        {getAutomatedColumnValue(cell, i, rowIdx, 'perf')}
                                                    </Text>
                                                </View>
                                            ))}
                                        </View>
                                    ))}
                                </View>
                            );
                        })()}

                        {/* Risk Indicators */}
                        {risikoindikatoren && Array.isArray(risikoindikatoren.labels) && risikoindikatoren.labels.length > 0 && (
                            <View style={{ marginTop: chartBlockMt }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    <Text style={pdfStyles.sectionTitle}>{sectionHeaders.risikoindikatoren}</Text>
                                </View>
                                {/* Header row */}
                                <View style={{ flexDirection: 'row', marginTop: chartBlockMt, paddingBottom: 0 }}>
                                    {risikoindikatoren.labels.map((label: string, index: number) => (
                                        <View key={index} style={{ flex: 1 }}>
                                            <Text style={{
                                                fontSize: 7,
                                                fontWeight: 'bold',
                                                color: '#000000',
                                                textAlign: index === 0 ? 'left' : 'center',
                                                lineHeight: 1,
                                            }}>
                                                {label}
                                            </Text>
                                        </View>
                                    ))}
                                </View>
                                {/* Value rows */}
                                {Array.isArray(risikoindikatoren.values) && risikoindikatoren.values.map((row: any[], rowIndex: number) => (
                                    <View key={rowIndex} style={{ flexDirection: 'row', marginTop: 2, gap: 2 }}>
                                        {Array.isArray(row) && row.map((cell: any, colIndex: number) => (
                                            <View key={colIndex} style={{
                                                flex: 1,
                                                backgroundColor: '#D2D2D4',
                                                paddingVertical: 1,
                                                paddingHorizontal: 4,
                                            }}>
                                                <Text style={{
                                                    fontSize: 7,
                                                    color: '#000000',
                                                    textAlign: colIndex === 0 ? 'left' : 'center',
                                                    lineHeight: 1,
                                                }}>
                                                    {getAutomatedColumnValue(cell, colIndex, rowIndex, 'risk')}
                                                </Text>
                                            </View>
                                        ))}
                                    </View>
                                ))}
                            </View>
                        )}
                        </View>

                        <View style={pdfStyles.page1ChartFootnotesInColumn} wrap={false}>
                            <Text style={pdfStyles.page1ChartFootnotesText}>
                                {resolveFussnoteText(effectiveData.fussnote)}
                            </Text>
                        </View>
                        </View>
                    </View>
                </View>
            </Page>

            {/* Second Page */}
            <Page
                size="A4"
                style={pdfStyles.page}
            >
                {renderHeader(false)}

                <View style={[pdfStyles.twoColumnLayout, { alignItems: 'flex-start' }]}>
                    {/* Left Column - Text sections */}
                    <View style={[pdfStyles.leftColumn, { alignSelf: 'flex-start' }]}>
                        {isPvmGps ? (
                            <PvmPage2LeftPdf
                                pdfStyles={pdfStyles}
                                chancen={chancen}
                                risiken={risiken}
                                chancenTitle={sectionHeaders.chancen}
                                risikenTitle={sectionHeaders.risiken}
                                templateColor={templateColor}
                                renderContentHeader={renderContentHeader}
                            />
                        ) : (
                        <>
                        <View style={pdfStyles.leftColumnSection}>
                        {renderContentHeader(sectionHeaders.chancen, templateColor)}
                            <Text style={[pdfStyles.sectionText, { marginBottom: 0 }]}>{chancen}</Text>
                        </View>

                        <View style={pdfStyles.leftColumnSection}>
                        {renderContentHeader(sectionHeaders.risiken, templateColor)}
                            <Text style={[pdfStyles.sectionText, { marginBottom: 0 }]}>{risiken}</Text>
                        </View>

                        {!omitEthikKontroverse && (
                            <>
                                <View style={pdfStyles.leftColumnSection}>
                        {renderContentHeader(sectionHeaders.ethik, templateColor)}
                                    <Text style={[pdfStyles.sectionText, { marginBottom: 0 }]}>{ethik}</Text>
                                </View>

                        {/* Business Table - Kontroverse Geschäftsfelder */}
                        {businessTable.length > 0 && (
                            <View style={pdfStyles.businessTableContainer}>
                                {renderContentHeader(sectionHeaders.businessTable, templateColor)}
                                {/* Header row */}
                                <View style={pdfStyles.businessTableHeaderRow}>
                                    <View style={{ width: '8%' }}>
                                        <Text style={pdfStyles.businessTableHeaderCell}> </Text>
                                    </View>
                                    <View style={{ width: '46%' }}>
                                        <Text style={pdfStyles.businessTableHeaderCell}>Ausschlusskriterien</Text>
                                    </View>
                                    <View style={{ width: '24%' }}>
                                        <Text style={pdfStyles.businessTableHeaderCell}>{'Kein Umsatz'}</Text>
                                    </View>
                                    <View style={{ width: '22%' }}>
                                        <Text style={pdfStyles.businessTableHeaderCell}>{'Umsatz < 5%'}</Text>
                                    </View>
                                </View>
                                {/* Body rows */}
                                {businessTable.map((item: any, index: number) => {
                                    const iconUrl = getEsgItemImageUrl(item, index);
                                    // Only use base64-cached version for react-pdf (raw URLs fail due to CORS)
                                    const iconSrc = iconUrl ? esgIconCache?.get(iconUrl) : undefined;
                                    return (
                                    <View key={index} style={pdfStyles.businessTableBodyRow}>
                                        <View style={{ width: '8%', alignItems: 'center', justifyContent: 'center' }}>
                                            {iconSrc ? (
                                                <Image
                                                    src={iconSrc}
                                                    style={pdfStyles.businessTableIcon}
                                                />
                                            ) : (
                                                <Text style={pdfStyles.businessTableBodyCell}> </Text>
                                            )}
                                        </View>
                                        <View style={{ height: '100%', width: '46%', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                            <Text style={pdfStyles.businessTableBodyCell}>{item.label || ''}</Text>
                                        </View>
                                        <View style={{ width: '24%' }}>
                                            <Text style={pdfStyles.businessTableBodyCell}>{item.no_turnover}%</Text>
                                        </View>
                                        <View style={{ width: '22%' }}>
                                            <Text style={pdfStyles.businessTableBodyCell}>{item.sales_volume}%</Text>
                                        </View>
                                    </View>
                                    );
                                })}
                            </View>
                                )}
                            </>
                        )}
                        </>
                        )}
                    </View>

                    {/* Right Column - Charts (native react-pdf rendering matching screenshot) */}
                    <View style={[pdfStyles.rightColumn, { alignSelf: 'flex-start' }]}>
                        {isPvmGps ? (
                            <GpsPage2RightColumnPdf
                                effectiveData={effectiveData}
                                sectionHeaders={sectionHeaders}
                                pdfStyles={pdfStyles}
                                formatPercent={formatPercent}
                                getItemColor={getItemColor}
                                colorSets={colorSets}
                                pieChart1ImageDataUrl={effectiveData.pieChart1ImageDataUrl}
                                pieChart2ImageDataUrl={effectiveData.pieChart2ImageDataUrl}
                                templateColor={templateColor}
                            />
                        ) : (
                        <>
                        {/* Top 5 Stocks - Legend section (flex) + Bar section (fixed 80px) */}
                        {Array.isArray(gesamtportfolio) && gesamtportfolio.length > 0 && (() => {
                            const maxVal = Math.max(...gesamtportfolio.map((d: any) => +d.value));
                            return (
                                <View style={{ marginBottom: 12 }}>
                                    <Text style={pdfStyles.chartTitle}>
                                        {pieHeading('gesamtportfolio')}
                                    </Text>
                                    <View style={{ flexDirection: 'row' }}>
                                        {/* Legend section */}
                                        <View style={{ flex: 1, paddingRight: 4 }}>
                                            {gesamtportfolio.map((item: any, index: number) => (
                                                <View
                                                    key={index}
                                                    style={{
                                                        flexDirection: 'row',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#D2D2D4',
                                                        marginBottom: 4,
                                                        paddingHorizontal: 3,
                                                        height: 12,
                                                    }}
                                                >
                                                    <Text style={{ flex: 1, fontSize: 7.5, color: '#000', lineHeight: 1 }}>
                                                        {item.label}
                                                    </Text>
                                                    <Text style={{ fontSize: 7.5, textAlign: 'right', color: '#000', lineHeight: 1 }}>
                                                        {formatPercent(item.value)}
                                                    </Text>
                                                </View>
                                            ))}
                                        </View>
                                        {/* Chart (bar) section - fixed width */}
                                        <View style={{ width: 80 }}>
                                            {gesamtportfolio.map((item: any, index: number) => (
                                                <View
                                                    key={index}
                                                    style={{
                                                        justifyContent: 'center',
                                                        marginBottom: 4,
                                                        height: 12,
                                                    }}
                                                >
                                                    <View
                                                        style={{
                                                            width: `${(+item.value / maxVal) * 100}%`,
                                                            height: 12,
                                                            backgroundColor: templateColor,
                                                        }}
                                                    />
                                                </View>
                                            ))}
                                        </View>
                                    </View>
                                </View>
                            );
                        })()}

                        {getPieChartDisplayOrder(template?._id).map((slot) => {
                            const pieData = effectiveData[slot.field] ?? (slot.field === 'branchen' ? branchen : slot.field === 'regionen' ? regionen : slot.field === 'marktkapitalisierung' ? marktkapitalisierung : pie_addition);
                            const pieImageUrl = effectiveData[slot.previewImageKey];
                            if (!Array.isArray(pieData) || pieData.length === 0) return null;
                            return (
                            <View key={slot.field} style={{ marginBottom: 12 }}>
                                <Text style={pdfStyles.chartTitle}>
                                    {pieHeading(slot.key)}
                                </Text>
                                <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                                    <View style={{ flex: 1, paddingRight: 4 }}>
                                        {pieData.map((item: any, index: number) => (
                                            <View
                                                key={index}
                                                style={{
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    backgroundColor: '#D2D2D4',
                                                    marginBottom: 4,
                                                    paddingHorizontal: 3,
                                                    height: 12,
                                                }}
                                            >
                                                <Text style={{ flex: 1, fontSize: 7.5, color: '#000', lineHeight: 1 }}>
                                                    {item.label}
                                                </Text>
                                                <Text style={{ fontSize: 7.5, textAlign: 'right', paddingRight: 5, width: 40, color: '#000', lineHeight: 1 }}>
                                                    {formatPercent(item.value)}
                                                </Text>
                                                <View style={{ width: 6, height: 6, backgroundColor: getItemColor(item, index, colorSets) }} />
                                            </View>
                                        ))}
                                    </View>
                                    {/* Chart (donut) section - fixed width */}
                                    <View style={{ width: 80 }}>
                                        {pieImageUrl ? (
                                            <Image src={pieImageUrl} style={{ width: 80, height: 80 }} />
                                        ) : (
                                            <View style={{ width: 80, height: 80, backgroundColor: '#f0f0f0' }}>
                                                <Text style={{ fontSize: 6, textAlign: 'center', paddingTop: 35 }}>Loading...</Text>
                                            </View>
                                        )}
                                    </View>
                                </View>
                            </View>
                            );
                        })}
                        </>
                        )}
                                            </View>
                                    </View>

                {isPvmGps ? (
                    <PvmPage2AwardImagesRowPdf
                        pdfStyles={pdfStyles}
                        imageSrc={
                            page2ImageSrc ||
                            (typeof effectiveData.page2ImageUrl === 'string'
                                ? effectiveData.page2ImageUrl
                                : undefined)
                        }
                        imageSrc2={
                            page2ImageSrc2 ||
                            (typeof effectiveData.page2ImageUrl2 === 'string'
                                ? effectiveData.page2ImageUrl2
                                : undefined)
                        }
                        imageSrc3={
                            page2ImageSrc3 ||
                            (typeof effectiveData.page2ImageUrl3 === 'string'
                                ? effectiveData.page2ImageUrl3
                                : undefined)
                        }
                    />
                ) : null}

                {/* PVM: pin disclaimer to page bottom; paddingTop = modest min gap under awards */}
                <View
                    style={[
                        pdfStyles.disclaimer,
                        isPvmGps ? { marginTop: 'auto', paddingTop: 24 } : {},
                    ]}
                    wrap={false}
                >
                    <Text>
                        <Text style={pdfStyles.disclaimerTitle}>Disclaimer: </Text>
                        {resolveDisclaimerText(effectiveData.disclaimer)}
                    </Text>
                </View>
            </Page>

        </Document>
    );
};

export const PdfPreviewDialog: FC<PdfPreviewDialogProps> = ({
    open,
    onClose,
    data,
    template,
    title = 'PDF Preview',
    isin,
    until,
}) => {
    const [logoSrc, setLogoSrc] = useState<string | undefined>(undefined);
    const [rightLogoSrc, setRightLogoSrc] = useState<string | undefined>(undefined);
    const [manImageSrc, setManImageSrc] = useState<string | undefined>(undefined);
    const [page2ImageSrc, setPage2ImageSrc] = useState<string | undefined>(undefined);
    const [page2ImageSrc2, setPage2ImageSrc2] = useState<string | undefined>(undefined);
    const [page2ImageSrc3, setPage2ImageSrc3] = useState<string | undefined>(undefined);
    const manImageUrlResolved = resolveManImageUrl(template?._id, data?.manImageUrl, template?.manImageUrl);
    const [cellDataCache, setCellDataCache] = useState<Map<string, any>>(new Map());
    const cellDataCacheRef = useRef<Map<string, any>>(new Map());
    const dynamicTableFetchKeysRef = useRef<Set<string>>(new Set());
    const kvFetchStartedRef = useRef<Set<string>>(new Set());
    const [esgIconCache, setEsgIconCache] = useState<Map<string, string>>(new Map());
    const [chartImageSrc, setChartImageSrc] = useState<string | undefined>(undefined);

    useEffect(() => {
        cellDataCacheRef.current = cellDataCache;
    }, [cellDataCache]);

    useEffect(() => {
        if (open) {
            const empty = new Map<string, any>();
            cellDataCacheRef.current = empty;
            dynamicTableFetchKeysRef.current = new Set();
            kvFetchStartedRef.current = new Set();
            setCellDataCache(empty);
            return;
        }
        cellDataCacheRef.current = new Map();
        dynamicTableFetchKeysRef.current = new Set();
        kvFetchStartedRef.current = new Set();
        setCellDataCache(new Map());
    }, [open]);

    const getDynamicSignature = useCallback((config: any): string => {
        try {
            return JSON.stringify(config?.fields || []);
        } catch {
            return '';
        }
    }, []);

    /**
     * Convert a Blob to a base64 data URL with correct MIME type detection.
     * Production S3 proxy may return application/octet-stream; this helper
     * detects the actual image type from magic bytes and fixes the MIME header.
     */
    const blobToDataUrl = useCallback(async (blob: Blob, url?: string): Promise<string> => {
        return new Promise((resolve, reject) => {
            let mimeType = blob.type;

            // If blob type is missing or generic, infer from URL extension
            if (!mimeType || mimeType === 'application/octet-stream') {
                if (url) {
                    const lowerUrl = url.toLowerCase();
                    if (lowerUrl.includes('.png') || lowerUrl.includes('png')) mimeType = 'image/png';
                    else if (lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg') || lowerUrl.includes('jpg') || lowerUrl.includes('jpeg')) mimeType = 'image/jpeg';
                    else if (lowerUrl.includes('.gif') || lowerUrl.includes('gif')) mimeType = 'image/gif';
                    else if (lowerUrl.includes('.webp') || lowerUrl.includes('webp')) mimeType = 'image/webp';
                    else mimeType = 'image/png'; // default
                } else {
                    mimeType = 'image/png';
                }
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                const base64Data = result.includes(',') ? result.split(',')[1] : result;

                // Detect actual image type from base64 magic bytes
                let detectedMime = mimeType;
                if (base64Data) {
                    if (base64Data.startsWith('/9j/4AAQ') || base64Data.startsWith('/9j/')) detectedMime = 'image/jpeg';
                    else if (base64Data.startsWith('iVBORw0KGgo')) detectedMime = 'image/png';
                    else if (base64Data.startsWith('R0lGODlh')) detectedMime = 'image/gif';
                    else if (base64Data.startsWith('UklGR')) detectedMime = 'image/webp';
                }

                // Rebuild data URL with correct MIME type
                if (result.startsWith('data:application/octet-stream') || (result.startsWith('data:') && !result.startsWith(`data:${detectedMime}`))) {
                    resolve(`data:${detectedMime};base64,${base64Data}`);
                } else {
                    resolve(result);
                }
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }, []);

    // Pre-fetch business table icons and convert to base64 data URLs for react-pdf
    useEffect(() => {
        if (template?._id === TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE) return;
        const businessTable = Array.isArray(data?.business_table) ? data.business_table : [];
        const S3_PREFIX = 'https://cixon-finance-docs.s3.amazonaws.com/';
        // Collect all unique icon URLs that haven't been cached yet
        const urlsToFetch: string[] = [];
        businessTable.forEach((item: any, index: number) => {
            const fullUrl = getEsgItemImageUrl(item, index);
            if (fullUrl && !esgIconCache.has(fullUrl) && !urlsToFetch.includes(fullUrl)) {
                urlsToFetch.push(fullUrl);
            }
        });
        // Also include default icons (for rows without a custom image)
        DEFAULT_ESG_ICONS.forEach((iconPath) => {
            const fullUrl = `${API_SERVER}/${iconPath}`;
            if (!esgIconCache.has(fullUrl) && !urlsToFetch.includes(fullUrl)) {
                urlsToFetch.push(fullUrl);
            }
        });
        if (urlsToFetch.length === 0) return;
        let cancelled = false;

        const fetchIcons = async () => {
            const newCache = new Map(esgIconCache);
            await Promise.all(
                urlsToFetch.map(async (fullUrl) => {
                    try {
                        let blob: Blob;
                        if (fullUrl.startsWith(S3_PREFIX)) {
                            const s3Key = fullUrl.replace(S3_PREFIX, '');
                            blob = await http.getBlob(`/docs/files/download/${s3Key}`);
                        } else {
                            let relativePath = fullUrl.replace(API_SERVER, '').replace(/^\/\.\//, '/');
                            if (relativePath.startsWith('./')) {
                                relativePath = '/' + relativePath.slice(2);
                            }
                            blob = await http.getBlob(relativePath);
                        }
                        const dataUrl = await blobToDataUrl(blob, fullUrl);
                        if (dataUrl) {
                            newCache.set(fullUrl, dataUrl);
                        }
                    } catch (e) {
                        console.error(`Failed to load ESG icon: ${fullUrl}`, e);
                    }
                }),
            );
            if (!cancelled) {
                setEsgIconCache(newCache);
            }
        };
        fetchIcons();
        return () => { cancelled = true; };
    }, [data?.business_table, blobToDataUrl, template?._id]);

    // Fetch template icon_url and convert to base64 data URL for react-pdf
    useEffect(() => {
        const isPvmGps = isPvmGpsTemplate(template?._id);
        const iconUrl = isPvmGps
            ? resolvePvmGpsLeftLogoUrl(template?.icon_url as string | undefined)
            : (template?.icon_url as string | undefined);
        if (!iconUrl) {
            setLogoSrc(undefined);
            return;
        }
        let cancelled = false;
        const fetchLogo = async () => {
            try {
                if (isPvmGps) {
                    const dataUrl = await fetchImageForPdf(iconUrl);
                    if (!cancelled) setLogoSrc(dataUrl || iconUrl);
                    return;
                }
                const s3Key = iconUrl.replace('https://cixon-finance-docs.s3.amazonaws.com/', '');
                const downloadUrl = `/docs/files/download/${s3Key}`;
                const logoBlob: Blob = await http.getBlob(downloadUrl);
                const dataUrl = await blobToDataUrl(logoBlob, iconUrl);
                if (!cancelled && dataUrl) {
                    setLogoSrc(dataUrl);
                }
            } catch (e) {
                console.error('Failed to load logo for PDF:', e);
                if (!cancelled) setLogoSrc(iconUrl);
            }
        };
        fetchLogo();
        return () => { cancelled = true; };
    }, [template?._id, template?.icon_url, blobToDataUrl]);

    useEffect(() => {
        if (!isPvmGpsTemplate(template?._id)) {
            setRightLogoSrc(undefined);
            return;
        }
        let cancelled = false;
        const fetchRightLogo = async () => {
            const dataUrl = await fetchImageForPdf(PVM_GPS_RIGHT_LOGO);
            if (!cancelled && dataUrl) setRightLogoSrc(dataUrl);
        };
        fetchRightLogo();
        return () => { cancelled = true; };
    }, [template?._id]);

    // Prefetch PVM page-2 left images (react-pdf cannot reliably load remote S3 URLs)
    useEffect(() => {
        if (!open || !isPvmGpsTemplate(template?._id)) {
            setPage2ImageSrc(undefined);
            setPage2ImageSrc2(undefined);
            setPage2ImageSrc3(undefined);
            return;
        }
        let cancelled = false;
        const load = async (url: unknown, setSrc: (v: string | undefined) => void) => {
            const raw = typeof url === 'string' ? url.trim() : '';
            if (!raw) {
                setSrc(undefined);
                return;
            }
            if (raw.startsWith('data:')) {
                setSrc(raw);
                return;
            }
            const dataUrl = await fetchImageForPdf(raw);
            if (!cancelled) setSrc(dataUrl || undefined);
        };
        load(data?.page2ImageUrl, setPage2ImageSrc);
        load(data?.page2ImageUrl2, setPage2ImageSrc2);
        load(data?.page2ImageUrl3, setPage2ImageSrc3);
        return () => {
            cancelled = true;
        };
    }, [open, template?._id, data?.page2ImageUrl, data?.page2ImageUrl2, data?.page2ImageUrl3]);

    // Fetch Monatskommentar portrait — same S3 → /docs/files/download → blobToDataUrl as ESG icons / logo
    useEffect(() => {
        if (!open || !manImageUrlResolved) {
            setManImageSrc(undefined);
            return;
        }
        const isPlaceholder =
            manImageUrlResolved === '/images/intro.png' ||
            manImageUrlResolved.endsWith('/images/intro.png');
        // PVM: skip default placeholder; only load a real uploaded/custom image
        if (isPvmGpsTemplate(template?._id) && isPlaceholder) {
            setManImageSrc(undefined);
            return;
        }
        if (manImageUrlResolved.startsWith('data:')) {
            setManImageSrc(manImageUrlResolved);
            return;
        }

        let cancelled = false;

        const fetchManImage = async () => {
            try {
                const iconUrl = manImageUrlResolved;
                let blob: Blob;
                if (iconUrl.startsWith(S3_PREFIX) || iconUrl.includes('cixon-finance-docs.s3.amazonaws.com')) {
                    const s3Key = iconUrl.replace(S3_PREFIX, '').split('?')[0].split('#')[0];
                    blob = await http.getBlob(`/docs/files/download/${s3Key}`);
                } else {
                    let relativePath = iconUrl.replace(API_SERVER, '').replace(/^\/\.\//, '/');
                    if (relativePath.startsWith('./')) {
                        relativePath = '/' + relativePath.slice(2);
                    }
                    blob = await http.getBlob(relativePath);
                }
                const dataUrl = await blobToDataUrl(blob, iconUrl);
                if (!cancelled && dataUrl) {
                    setManImageSrc(dataUrl);
                }
            } catch (e) {
                console.error('Failed to load man image for PDF:', manImageUrlResolved, e);
                if (!cancelled) setManImageSrc(undefined);
            }
        };
        fetchManImage();
        return () => {
            cancelled = true;
        };
    }, [open, manImageUrlResolved, template?._id]);

    // Fetch chart image (chartImageUrl or chartImageDataUrl) and convert to base64 data URL for react-pdf
    useEffect(() => {
        let active = true;

        async function loadChartImage() {
            const chartImageUrl = data?.chartImageUrl || data?.chartImageDataUrl;

            if (!chartImageUrl) {
                if (active) setChartImageSrc(undefined);
                return;
            }

            // Already a base64 data URL – use directly
            if (chartImageUrl.startsWith('data:')) {
                if (active) setChartImageSrc(chartImageUrl);
                return;
            }

            try {
                const s3Key = chartImageUrl.replace('https://cixon-finance-docs.s3.amazonaws.com/', '');
                const downloadUrl = `/docs/files/download/${s3Key}`;
                const blob: Blob = await http.getBlob(downloadUrl);
                const dataUrl = await blobToDataUrl(blob, chartImageUrl);
                if (active && dataUrl) {
                    setChartImageSrc(dataUrl);
                }
            } catch (e) {
                console.error('Failed to load chart image for PDF:', e);
                if (active) setChartImageSrc(undefined);
            }
        }

        loadChartImage();
        return () => { active = false; };
    }, [data?.chartImageUrl, data?.chartImageDataUrl, blobToDataUrl]);

    const resolveDynamicIsin = useCallback(
        (config: any, fallbackIsin?: string): string => {
            const base = fallbackIsin ?? isin;
            if (base) return base;
            const fieldWithIsin = config?.fields?.find(
                (field: any) => typeof field?.isin === 'string' && field.isin.trim().length > 0,
            );
            return fieldWithIsin?.isin?.trim() || '';
        },
        [isin],
    );

    const fetchDynamicTable = useCallback(
        async (values: any[][], prefix: string) => {
            if (!values?.length || !Array.isArray(values)) return;

            const fetchKey = `${prefix}-${until || ''}-${getDynamicSignature({ fields: values })}`;
            if (dynamicTableFetchKeysRef.current.has(fetchKey)) return;
            dynamicTableFetchKeysRef.current.add(fetchKey);

            const tableIsin =
                values
                    .flat()
                    .map((cell) => resolveDynamicIsin(cell))
                    .find((value) => value.length > 0) || isin;
            if (!tableIsin) return;

        try {
            const res = await client.query({
                query: GET_AUTOMATED_COLUMNS,
                    variables: {
                        param: { data: normalizeAutomatedColumnsInput(values), isin: tableIsin, until },
                    },
            });
                const matrix = res.data?.getAutomatedColumns;
                if (!Array.isArray(matrix)) return;

            setCellDataCache((prev) => {
                const newCache = new Map(prev);
                    values.forEach((row: any[], rowIndex: number) => {
                        if (!Array.isArray(row)) return;
                        row.forEach((cell: any, colIndex: number) => {
                            if (cell?.isDynamic && cell?.fields?.length) {
                                const cellKey = `${prefix}-${rowIndex}-${colIndex}-${getDynamicSignature(cell)}`;
                                newCache.set(cellKey, matrix[rowIndex]?.[colIndex]);
                            }
                        });
                    });
                    cellDataCacheRef.current = newCache;
                return newCache;
            });
        } catch (error) {
                console.error('Failed to fetch dynamic table data:', error);
                dynamicTableFetchKeysRef.current.delete(fetchKey);
            }
        },
        [isin, until, getDynamicSignature, resolveDynamicIsin],
    );

    const fetchKvCell = useCallback(
        async (config: any, kvKey: string, columnIsin?: string, cacheSuffix?: string) => {
            const effIsin = resolveDynamicIsin(config, columnIsin);
            if (!effIsin || !config?.isDynamic || !config?.fields?.length) return;
            const key = `${kvKey}-${getDynamicSignature(config)}${cacheSuffix ? `-${cacheSuffix}` : ''}`;
            if (cellDataCacheRef.current.has(key) || kvFetchStartedRef.current.has(key)) return;
            kvFetchStartedRef.current.add(key);
        try {
            const res = await client.query({
                query: GET_AUTOMATED_COLUMNS,
                    variables: { param: { data: [[config]], isin: effIsin, until } },
            });
            const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
            setCellDataCache((prev) => {
                const next = new Map(prev);
                next.set(key, fetchedValue);
                    cellDataCacheRef.current = next;
                return next;
            });
        } catch (e) {
                kvFetchStartedRef.current.delete(key);
        }
        },
        [until, getDynamicSignature, resolveDynamicIsin],
    );

    // Fetch all dynamic cells from both tables when dialog opens or data changes
    useEffect(() => {
        if (!open) return;

        const fetchAllDynamicCells = async () => {
            const promises: Promise<void>[] = [];

            const perf = data?.wertentwicklung_nach_perioden;
            if (perf?.labels && Array.isArray(perf.values)) {
                promises.push(fetchDynamicTable(perf.values, 'perf').then(() => undefined));
            }

            const risk = data?.risikoindikatoren;
            if (risk?.labels && Array.isArray(risk.values)) {
                promises.push(fetchDynamicTable(risk.values, 'risk').then(() => undefined));
            }

            // Preise: dynamic key-value fields
            const mepreisCfg = data?.preise?.mepreis;
            const fondsvolumenCfg = data?.preise?.fondsvolumen;
            const ausgabeCfg = data?.preise?.ausgabepreis;
            const ruecknahmeCfg = data?.preise?.ruecknahmepreis;
            const isDualShare = template?._id === TEMPLATE_ID_DUAL_SHARE_CLASS;
            if (isDualShare) {
                if (ausgabeCfg?.isDynamic) {
                    promises.push(fetchKvCell(ausgabeCfg, 'kv-preise-ausgabepreis').then(() => undefined));
                }
                if (ruecknahmeCfg?.isDynamic) {
                    promises.push(fetchKvCell(ruecknahmeCfg, 'kv-preise-ruecknahmepreis').then(() => undefined));
                }
            } else if (mepreisCfg?.isDynamic) {
                promises.push(fetchKvCell(mepreisCfg, 'kv-preise-mepreis').then(() => undefined));
            }
            // PVM left column uses ruecknahmepreis as NAV (Anteilwert)
            if (isPvmGpsTemplate(template?._id) && ruecknahmeCfg?.isDynamic) {
                promises.push(fetchKvCell(ruecknahmeCfg, 'kv-preise-ruecknahmepreis').then(() => undefined));
            }
            if (fondsvolumenCfg?.isDynamic) {
                promises.push(fetchKvCell(fondsvolumenCfg, 'kv-preise-fondsvolumen').then(() => undefined));
            }

            const shareCols = data?.shareClassColumns;
            if (
                template?._id === TEMPLATE_ID_DUAL_SHARE_CLASS &&
                Array.isArray(shareCols) &&
                shareCols.length >= 2
            ) {
                shareCols.forEach((col: any, i: number) => {
                    const colIsin = String(col?.stammdaten?.isin || col?.isin || template?.isins?.[i] || isin);
                    const suff = `sc${i}`;
                    if (col?.preise?.ausgabepreis?.isDynamic) {
                        promises.push(
                            fetchKvCell(col.preise.ausgabepreis, 'kv-preise-ausgabepreis', colIsin, suff).then(() => undefined),
                        );
                    }
                    if (col?.preise?.ruecknahmepreis?.isDynamic) {
                        promises.push(
                            fetchKvCell(col.preise.ruecknahmepreis, 'kv-preise-ruecknahmepreis', colIsin, suff).then(
                                () => undefined,
                            ),
                        );
                    }
                    if (col?.preise?.mepreis?.isDynamic) {
                        promises.push(
                            fetchKvCell(col.preise.mepreis, 'kv-preise-mepreis', colIsin, suff).then(() => undefined),
                        );
                    }
                    if (col?.preise?.fondsvolumen?.isDynamic) {
                        promises.push(
                            fetchKvCell(col.preise.fondsvolumen, 'kv-preise-fondsvolumen', colIsin, suff).then(
                                () => undefined,
                            ),
                        );
                    }
                });
            }

            await Promise.all(promises);
        };

        fetchAllDynamicCells();
    }, [
        open,
        isin,
        data?.wertentwicklung_nach_perioden,
        data?.risikoindikatoren,
        data?.preise?.mepreis,
        data?.preise?.ausgabepreis,
        data?.preise?.ruecknahmepreis,
        data?.preise?.fondsvolumen,
        data?.shareClassColumns,
        template?._id,
        template?.isins,
        until,
    ]);

    const downloadFileName = useMemo(
        () =>
            buildFactsheetDownloadFileName({
                customTemplate: data?.pdf_download_name,
                title:
                    data?.title ||
                    resolvePvmGpsFondTitle(template?._id, template?.fond) ||
                    template?.fond ||
                    template?.name ||
                    'Factsheet',
                asOf: resolveFactsheetDownloadAsOf(data) || until,
            }),
        [data, template?._id, template?.fond, template?.name, until],
    );

    const finalChartImageDataUrl = chartImageSrc || data?.chartImageDataUrl || data?.chartImageUrl;
    const factsheetPdf = (
        <FactsheetPDF
            data={{
                ...data,
                template,
                chartImageDataUrl: finalChartImageDataUrl,
            }}
            logoSrc={logoSrc}
            rightLogoSrc={rightLogoSrc}
            manImageSrc={manImageSrc}
            page2ImageSrc={page2ImageSrc}
            page2ImageSrc2={page2ImageSrc2}
            page2ImageSrc3={page2ImageSrc3}
            cellDataCache={cellDataCache}
            esgIconCache={esgIconCache}
        />
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} sx={dialogStyles.dialog}>
            <DialogTitle sx={dialogStyles.dialogTitle}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                    <Box>{title}</Box>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={dialogStyles.dialogContent}>
                <Box sx={dialogStyles.pdfContainer}>
                    {open ? (
                        <PdfPreviewWithTools document={factsheetPdf} fileName={downloadFileName} />
                    ) : null}
                </Box>
            </DialogContent>
        </Dialog>
    );
};
